/**
 * Global constants
 * Used across the application to ensure consistency and avoid typos
 */

export const ORDER_STATUS = {
  RECEIVED: "received",
  COUNTER_REJECTED: "counter_rejected",
  COUNTER_ACCEPTED: "counter_accepted",
  COUNTER_RECEIVED: "counter_received",
  COUNTER_SUBMITTED: "counter_submitted",
  COUNTER_SUBMITED: "counter_submited", // Keep for backward compatibility
  PENDING: "pending",
  EXPIRED: "expired",
  REJECTED: "rejected",
  ACCEPTED: "accepted",
};

// Action codes for API calls
export const ACTION_CODE = {
  COUNTER: 1,
  ACCEPT: 2,
  REJECT: 3,
  // EXPIRE: 4
  COUNTER_CANCEL: 4,
};

// Protocol types for bond orders
export const PROTOCOL_TYPES = {
  CLOB: "CLOB",
  RFQ: "RFQ",
  TRUMID_RFQ: "Trumid RFQ",
  MARKETAXESS: "MarketAxess RFQ", // updated label/value
};

export const PROTOCOL_TO_DEALER_PARAM = {
  [PROTOCOL_TYPES.CLOB]: "CLOB",
  [PROTOCOL_TYPES.RFQ]: "CLOB",
  [PROTOCOL_TYPES.TRUMID_RFQ]: "trumid",
  [PROTOCOL_TYPES.MARKETAXESS]: "MarketAxess",
};

// Bond types
export const BOND_TYPES = {
  TREASURY: "Treasury",
  MUNICIPALS: "Municipals",
  CORPORATES: "Corporates",
};

// Order side types
export const ORDER_SIDES = {
  BUY: "BUY",
  SELL: "SELL",
};

// Venue options
export const VENUES = {
  TRADING_DESK: "Trading Desk",
};

// Secondary sources used to identify special order-entry flows
export const ORDER_ENTRY_SECONDARY_SOURCES = {
  LADDER_LEVEL_SEND_TO_MARKET: "ladder-level-send-to-market",
  // Opened from the staging blotter "Source" column (characteristic / ladder name)
  // -> render characteristic filters as read-only chips instead of checkbox fields
  SOURCE_COLUMN_VIEW: "source-column-view",
};

// Roles
export const USER_ROLES = {
  SUPER_ADMIN: "SUPER_ADMIN",
  ADMIN: "ADMIN",
  PM: "PM",
  TRADER: "Trader",
};

const normalizeRole = (role) => String(role || "").replace(/^ROLE_/, "").toUpperCase();

export const isSuperAdminRole = (role) => normalizeRole(role) === USER_ROLES.SUPER_ADMIN;
export const isAdminRole = (role) =>
  [USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN].includes(normalizeRole(role));
export const canWriteSessionUserConfig = (role) => isSuperAdminRole(role);

export const EDI_FILE_PROCESS_FILTER_PORTAL_ID = "edi-file-process-filter-actions";

// Role-based access control (role required for specific features)
export const ROLE_BASED_ACCESS = {
  SESSION_USER_MANAGEMENT: "ADMIN_ONLY", // ADMIN/SUPER_ADMIN roles can access
  EDI_FILE_PROCESS_MANAGEMENT: "ADMIN_ONLY", // ADMIN/SUPER_ADMIN roles can access
};

// PMS/OMS feature flag based access control
export const FEATURE_FLAG_ACCESS = {
  WATCHLIST: "NO_FLAG_REQUIRED", // Accessible to all authenticated users
  POSITION_EXPLORER: "PMS_REQUIRED", // isPms flag must be enabled
  REBALANCE_WORKBENCH: "PMS_REQUIRED", // isPms flag must be enabled
};

// Helper function to check role-based access
export const hasRoleAccess = (role, feature) => {
  const requiredAccess = ROLE_BASED_ACCESS[feature];

  if (!requiredAccess) return true; // No role restriction

  if (requiredAccess === "ADMIN_ONLY") {
    return isAdminRole(role);
  }

  return true;
};

// Helper function to check feature flag based access (isPms/isOms)
export const hasFeatureFlagAccess = (feature, isPms = false, isOms = false) => {
  const requiredFlag = FEATURE_FLAG_ACCESS[feature];

  if (!requiredFlag) return true; // No feature flag restriction

  if (requiredFlag === "NO_FLAG_REQUIRED") {
    return true;
  }

  if (requiredFlag === "PMS_REQUIRED") {
    return !!isPms;
  }

  if (requiredFlag === "OMS_REQUIRED") {
    return !!isOms;
  }

  return true;
};

// Combined helper function for complete access check
export const hasAccessToFeature = (
  role,
  feature,
  isPms = false,
  isOms = false,
) => {
  // ADMIN/SUPER_ADMIN roles have access to all admin screens (always)
  if (isAdminRole(role)) {
    // For Session User Management, admin role is required
    if (feature === "SESSION_USER_MANAGEMENT") return true;
    // For other features, admin still needs to check feature flags unless it's admin-only
    return hasFeatureFlagAccess(feature, isPms, isOms);
  }

  // Check both role-based and feature flag based access
  const hasRole = hasRoleAccess(role, feature);
  const hasFlag = hasFeatureFlagAccess(feature, isPms, isOms);

  return hasRole && hasFlag;
};

// TruMid RFQ specific constants
export const TRUMID_RFQ_INQUIRY_TYPES = {
  ASAP: "ASAP",
  BIN: "BIN",
};

export const TRUMID_PRICE_TYPES = {
  PRICE: "PRICE",
  SPREAD: "SPREAD",
};

export const TRUMID_STIPULATION_TYPES = {
  HEDGE: "HEDGE",
  SPOT: "SPOT",
};

export const TRUMID_PARTIAL_EXECUTION = {
  YES: "Y",
  NO: "N",
};

/**
 * Most bonds one List RFQ may carry.
 *
 * Ours, NOT Trumid's. The Trumid RFQ spec puts no cap on the NoRelatedSym <146> group and
 * neither does our backend, so nothing outside this constant refuses a larger list - it is a
 * product decision to keep one enquiry to a size a trader can actually work through.
 */
export const MAX_LIST_RFQ_BONDS = 25;

/**
 * List name for a List RFQ. Ours only - never sent to Trumid (the RFQ spec has no list-name
 * tag). The backend stores it once per list (list_order_seq.list_name, VARCHAR(40)) and allows
 * it to be empty, so whether a name is REQUIRED is decided here: flip the flag to make the
 * List Name field mandatory before Send.
 */
export const LIST_RFQ_NAME_REQUIRED = false;
export const MAX_LIST_RFQ_NAME_LENGTH = 40;

/**
 * The name a new list starts with, per protocol. Prefilled so every list gets a readable name
 * without the trader typing one; they can change or clear it.
 */
export const LIST_RFQ_DEFAULT_NAMES = {
  [PROTOCOL_TYPES.TRUMID_RFQ]: "Trumid RFQ List",
  [PROTOCOL_TYPES.MARKETAXESS]: "Market Axess RFQ List",
};

export const defaultListName = (protocol) => LIST_RFQ_DEFAULT_NAMES[protocol] || "";

// MarketAxess default expiry time in UTC (HH:MM:SS.mmm)
// This is displayed in local time on screen via the timeConversions utility
export const MKTX_DEFAULT_EXPIRY_TIME_UTC = "20:57:00.000";

// MarketAxess specific constants
export const MKTX_DEALER_LIST_MODES = {
  INCLUDE_ONLY: "Include Only These Dealers",
  INCLUDE_AT_LEAST: "Include At Least These Dealers",
};

export const MKTX_INQUIRY_TYPES = {
  ASAP: "ASAP",
  BIN: "BIN",
};

export const MKTX_LEVEL_VARIATION_TYPES = {
  LEVEL_VAR: "Level Var",
  LEVEL_VAR_PERCENT: "Level Var Percent",
  LEVEL_VAR_FROM_MID: "Level Var From Mid",
  LEVEL_VAR_PERCENT_FROM_MID: "Level Var Percent From Mid",
};

export const MKTX_PRICE_TYPES = {
  PRICE: "PRICE",
  SPREAD: "SPREAD",
};

// New: MarketAxess pricing process options
export const MKTX_PRICING_PROCESSES = {
  ONESTEP: "ONESTEP",
  DELAYEDSPOT: "DELAYEDSPOT",
};

export const MKTX_PRICING_PROCESS_DISPLAY = {
  [MKTX_PRICING_PROCESSES.ONESTEP]: "One Step",
  [MKTX_PRICING_PROCESSES.DELAYEDSPOT]: "Delayed Spot",
};

export const MKTX_STRATEGY_TYPES = {
  PROVIDE: "PROVIDE",
  SEEK: "SEEK",
  TAKE: "TAKE",
};

export const mktxStrategyUsesDealers = (strategyType) =>
  strategyType !== MKTX_STRATEGY_TYPES.SEEK &&
  strategyType !== MKTX_STRATEGY_TYPES.PROVIDE;

export const MKTX_DEALER_LIST_MODE_DISPLAY_TO_SUBMIT = {
  [MKTX_DEALER_LIST_MODES.INCLUDE_ONLY]: "INCLUDE_ONLY_THESE_DEALERS",
  [MKTX_DEALER_LIST_MODES.INCLUDE_AT_LEAST]: "INCLUDE_AT_LEAST_THESE_DEALERS",
};

export const MKTX_DEALER_LIST_MODE_SUBMIT_TO_DISPLAY = Object.entries(
  MKTX_DEALER_LIST_MODE_DISPLAY_TO_SUBMIT,
).reduce((acc, [displayValue, submitValue]) => {
  acc[submitValue] = displayValue;
  return acc;
}, {});

export const MKTX_LEVEL_VARIATION_TYPE_DISPLAY_TO_SUBMIT = {
  [MKTX_LEVEL_VARIATION_TYPES.LEVEL_VAR]: "LEVEL_VAR",
  [MKTX_LEVEL_VARIATION_TYPES.LEVEL_VAR_PERCENT]: "LEVEL_VAR_PERCENT",
  [MKTX_LEVEL_VARIATION_TYPES.LEVEL_VAR_FROM_MID]: "LEVEL_VAR_FROM_MID",
  [MKTX_LEVEL_VARIATION_TYPES.LEVEL_VAR_PERCENT_FROM_MID]:
    "LEVEL_VAR_PERCENT_FROM_MID",
};

// MA Watchlist constants
export const MA_WATCHLIST_PERSISTED_CUSIPS_FETCH_SIZE = 50;
export const MA_STATIC_WATCHLIST_CUSIP_LIMIT = 100;
// Hard cap on how many bonds a dynamic watchlist's characteristics may match.
// Saving a dynamic watchlist whose applied criteria match more than this is blocked.
export const MA_DYNAMIC_WATCHLIST_CUSIP_LIMIT = 5000;
// Default page size used to prefetch the full MA Inventory dataset in a single call.
// Watchlist inventory is capped (static <= 100, dynamic <= 5000), so this fetches
// everything at once; the table then paginates and sorts the prefetched set client-side.
// Must stay >= the dynamic cap or the table silently paginates only the first page.
export const MA_INVENTORY_PREFETCH_PAGE_SIZE = 5000;
export const MA_STATIC_WATCHLIST_LIMIT_MESSAGE =
  "You can add up to 100 bonds to a watchlist. Please remove some items before adding more.";
export const MA_WATCHLIST_PROCESSING_MESSAGE =
  "Your watchlist is currently being updated to define characteristics. Please try again in a few moments.";
export const MA_DYNAMIC_WATCHLIST_LIMIT_MESSAGE =
  "A dynamic watchlist can include up to 5,000 bonds. Your characteristics currently match more than 5,000 — please refine them before saving.";
export const MA_WATCHLIST_FAILED_MESSAGE =
  "The last update didn't complete successfully. Please try again.";

export const EDI_FILE_PROCESS_STATUS = {
  SUCCESS: "SUCCESS",
  FAILED: "FAILED",
  PROCESSING: "PROCESSING",
};

